import { Button, VStack, HStack, Text, Box, Badge } from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { Class } from "../../../services/Dtos";
import useThemeColors from "../../../hooks/useThemeColors";
import useToaster from "../../../hooks/useToaster";
import useAxios from "../../../hooks/useAxios";
import { useStyles } from "../../../hooks/useStyles";
import CustomModal from "../../CustomUi/CustomModal";
import CustomAlert from "../../CustomUi/CustomAlert";
import CustomCheckBox from "../../CustomUi/CustomCheckBox";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  classes: Class[];
  selectedClassId: string;
}

const TemplateCustomizationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  classes,
  selectedClassId,
}) => {
  const [selectedClasses, setSelectedClasses] = useState<string[]>([
    selectedClassId,
  ]);
  const [isDownloading, setIsDownloading] = useState(false);
  const colors = useThemeColors();
  const { showSuccessToast, showErrorToast } = useToaster();
  const axiosClient = useAxios();
  const { scrollBar } = useStyles();

  // Update selected classes when selectedClassId changes
  useEffect(() => {
    if (selectedClassId && !selectedClasses.includes(selectedClassId)) {
      setSelectedClasses([selectedClassId]);
    }
  }, [selectedClassId]);

  const handleClassToggle = (classId: string) => {
    setSelectedClasses((prev) => {
      if (prev.includes(classId)) {
        return prev.filter((id) => id !== classId);
      } else {
        return [...prev, classId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedClasses(classes.map((c) => c.id));
  };

  const handleSelectNone = () => {
    setSelectedClasses([]);
  };

  const handleDownloadTemplate = async () => {
    if (selectedClasses.length === 0) {
      showErrorToast("Please select at least one class");
      return;
    }

    setIsDownloading(true);
    try {
      const response = await axiosClient.post(
        "bulkimport/download-result-template",
        { classIds: selectedClasses },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "results-upload-template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccessToast("Template downloaded successfully!");
      onClose();
    } catch (error) {
      showErrorToast("Failed to download template");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClose = () => {
    setSelectedClasses([selectedClassId]);
    setIsDownloading(false);
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Customize Template"
      handleClose={handleClose}
      handleAction={handleDownloadTemplate}
      actionText="Download Template"
      isLoading={isDownloading}
      hasChanges={selectedClasses.length > 0}
      hasValidationErrors={selectedClasses.length === 0}
      isDisabled={selectedClasses.length === 0 || isDownloading}
    >
      <VStack gap={6} align="stretch">
        {/* Instructions */}
        <CustomAlert
          title="Template Customization"
          message="Select the classes you want to include in your results template. The template will contain all subjects and behavioral subjects for the selected classes."
          status="info"
        />

        {/* Selection Controls */}
        <Box>
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="medium">Select Classes</Text>
            <HStack gap={2}>
              <Button size="sm" variant="ghost" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button size="sm" variant="ghost" onClick={handleSelectNone}>
                Select None
              </Button>
              <Badge colorPalette="blue" fontSize="sm" px={2} py={1}>
                {selectedClasses.length} selected
              </Badge>
            </HStack>
          </HStack>

          {/* Class List */}
          <Box
            maxH="300px"
            overflowY="auto"
            borderWidth={1}
            borderColor={colors.border.secondary}
            borderRadius="lg"
            p={3}
            css={scrollBar}
          >
            <VStack align="stretch" gap={3}>
              {classes.map((cls) => (
                <Box
                  key={cls.id}
                  p={3}
                  borderWidth={1}
                  borderColor={
                    selectedClasses.includes(cls.id)
                      ? colors.brand.primary
                      : colors.border.secondary
                  }
                  borderRadius="md"
                  bg={
                    selectedClasses.includes(cls.id)
                      ? `${colors.brand.primary}50`
                      : colors.background.secondary
                  }
                  transition="all 0.2s"
                >
                  <CustomCheckBox
                    checked={selectedClasses.includes(cls.id)}
                    onChange={() => handleClassToggle(cls.id)}
                    label={
                      <VStack align="start" gap={1} ml={2}>
                        <Text fontWeight="medium">{cls.className}</Text>
                        <HStack gap={2}>
                          <Badge colorPalette="blue" size="sm">
                            {cls.subjects.length} Subjects
                          </Badge>
                          <Badge colorPalette="green" size="sm">
                            {cls.behavioralSubjects.length} Behavioral
                          </Badge>
                        </HStack>
                      </VStack>
                    }
                  />
                </Box>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* Warning if no classes selected */}
        {selectedClasses.length === 0 && (
          <CustomAlert
            title="No classes selected"
            message="Please select at least one class to generate a template."
            status="warning"
          />
        )}
      </VStack>
    </CustomModal>
  );
};

export default TemplateCustomizationModal;
